import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import zafClient from "../../zafclient";
import AuthContext from "../../store/auth-context";
import OAuthClientContext from "../../store/oauthclient-context";
import classes from "./AuthForm.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import ErrorPage from "../../pages/ErrorPage";
import { API_NEW_URL } from "./../../constants/constants";
import OAuthAuthorize from "../OAuth/OAuthAuthorize";
import storage from "../../hooks/storage";
import { maybeRefreshDomainConfig } from "../../utils/domainConfig";

const AuthForm = () => {
  console.warn(window.origininfo);
  const authCtx = useContext(AuthContext);
  const oauthClientCtx = useContext(OAuthClientContext);
  const navigate = useNavigate();
  const [isConnecting, setisConnecting] = useState(true);
  let apiKey = false;
  const [errorObj, setErrorObj] = useState(null);

  // OAuth related state
  const [needsOAuth, setNeedsOAuth] = useState(false);
  const [zendeskUserInfo, setZendeskUserInfo] = useState(null);
  const [authResponseData, setAuthResponseData] = useState(null);

  const onAuthenticate = (data, userInfo) => {
    if (data.errorStatus) {
      if(data?.errorCode === 6704){ // don't have appropriate permissions
        // Token expired - clear auth data
        authCtx?.clearData();
      }
      setErrorObj(data);
    } else {
      console.info("onAuthenticate data",data?.data);

      // Authorization is required - check if user has Zendesk OAuth token
      if (!data?.hasZendeskOAuthToken) {
        // Need to show OAuth authorization flow
        // Set OAuth status as false so OAuthGate knows to skip API call
        oauthClientCtx.setOAuthStatusFromAuth(false, userInfo?.subdomain, userInfo?.isAdmin);
        setAuthResponseData(data);
        setZendeskUserInfo(userInfo);
        setNeedsOAuth(true);
        return;
      }

      // OAuth already done - set status so OAuthGate doesn't call check API
      oauthClientCtx.setOAuthStatusFromAuth(true, userInfo?.subdomain, userInfo?.isAdmin);

      // Proceed with normal login
      proceedWithLogin(data);
    }
  };

  const proceedWithLogin = (data) => {
    authCtx.login(
      data.userToken,
      data.tokenGeneratedAt,
      data.refreshToken,
      apiKey,
      data.userId,
      data
    );
    console.error(authCtx.isLoggedIn);
    navigate(
      "/invite?origin=" +
        window.origininfo.origin +
        "&app_guid=" +
        window.origininfo.app_guid,
      { replace: true }
    );
  };

  const handleOAuthComplete = () => {
    // OAuth completed successfully, proceed with login
    if (authResponseData) {
      proceedWithLogin(authResponseData);
    }
  };

  const authenticate = () => {
    console.log(isConnecting);
    try {
    // Get both currentUser AND currentAccount for subdomain
    zafClient.get(["currentUser", "currentAccount"]).then(function (data) {
      console.log("current user & current account",data); // { currentUser: {...}, currentAccount: {...}, errors: {...} }

      // Check for errors in the ZAF response
      if (data?.errors && Object.keys(data.errors).length > 0) {
        console.error("ZAF Client errors:", data.errors);
        setisConnecting(false);
        const errorMessages = Object.values(data.errors).join(", ");
        setErrorObj({
          errorStatus: -1,
          message: errorMessages || "Failed to get Zendesk user information"
        });
        return;
      }

      // Validate required data exists
      if (!data?.currentUser?.email || !data?.currentAccount?.subdomain) {
        console.error("Missing required Zendesk data:", data);
        setisConnecting(false);
        setErrorObj({
          errorStatus: -1,
          message: "Unable to retrieve Zendesk user or account information"
        });
        return;
      }

      // Zendesk exposes only a single `name` field, so split on the first
      // whitespace: first token → first_name, remainder → last_name.
      const fullName = (data?.currentUser?.name || "").trim();
      const spaceIdx = fullName.indexOf(" ");
      const firstName = spaceIdx === -1 ? fullName : fullName.slice(0, spaceIdx);
      const lastName = spaceIdx === -1 ? "" : fullName.slice(spaceIdx + 1).trim();

      let request = {
        email: data?.currentUser?.email,
        role: "",
        call_experience: "",
        department: "",
        first_name: firstName,
        last_name: lastName,
        subdomain:  data?.currentAccount?.subdomain,  // Send subdomain to backend
        from: 2, // for zendesk
        zendesk_user_id: data?.currentUser?.id, // used for adding comment inside ticket with upload image
      };
    //  const api_Key = "028640D4C1D842BD85A470FCA880F1E6"; // hexor domain

      var fetchItem = {
        url: API_NEW_URL + "auth",
        headers: { apikey: "{{setting.apikey}}" },
        secure: true,
        data: JSON.stringify(request),
        type: "POST",
        httpCompleteResponse: true,
        contentType: "application/json",
        cache: false,
      };
      console.info(fetchItem);
      zafClient.request(fetchItem).then(
        function (response) {
          setisConnecting(false);
          // SA-change check: dispatch a config-refresh signal if this
          // response's `configurationmodifiedat` header is newer than cache.
          maybeRefreshDomainConfig(response.headers);
          console.log(response.responseJSON); // body of the HTTP response
          console.log(response.status); // HTTP response status
          console.log(response.statusText); // Is either 'success' or 'error'
          console.log(response.headers); // HTTP response headers
          const respData = response.responseJSON;
          console.info(respData);
          if (response.status == 401) {
            const error = new Error(respData.data.Error);
            error.status = -1;
          } else if (response.status == 422) {
            const error = new Error(respData.data.Error);
            error.status = -11;
          } else {
            const error = new Error("Request failed!");
            error.status = -10;
          }

          if (
            response.headers.STATUS == -2 ||
            response.headers.STATUS == -3 ||
            response.headers.STATUS == -4
          ) {
            const error = new Error(response.headers.MESSAGE);
            error.status = response.headers.STATUS;
          }
          if (response.headers.STATUS == -5) {
            authCtx.clearData();
            const error = new Error(
              response.headers.MESSAGE + " Trying to connect again..."
            );
            error.status = response.headers.STATUS;
          }
          // Pass user info for OAuth flow
          const userInfo = {
            email: data.currentUser.email,
            name: data.currentUser.name,
            role: data.currentUser.role,
            subdomain: data.currentAccount.subdomain,
            isAdmin: data.currentUser.role === "admin",
            zendeskCurrentUser: data.currentUser, // pass entire user
          };
            // Store userInfo in localStorage for persistence across reloads
          storage.set('oauth_user_info', userInfo);
          onAuthenticate(respData, userInfo);
        },
        function (response) {
          setisConnecting(false);
          console.info(response.responseJSON);
          const errorVal = {
            errorStatus: -1,
            errorCode: response?.responseJSON?.data?.Errorcode,
            message: response?.responseJSON?.data?.Error,
          };
          onAuthenticate(errorVal);
          console.error(response.responseText);
        }
      );
    });
      } catch (error) {
          setisConnecting(false);
          console.error("ZAF get failed:", error?.message || error);
          onAuthenticate(error?.message || error);
      }
  };

  useEffect(
    authenticate, // <- function that will run on every dependency update
    [] // <-- empty dependency array
  );


  return (
    <section>
      {isConnecting &&
        <div className="form-group isconnectingcls" style={{ paddingTop: "19%" }}>
          <span className={classes.circlegif}>
            <FontAwesomeIcon icon={faCircleNotch} spin />
          </span>
          <p className={classes.circleText}>Connecting...</p>
        </div>
      }
      {needsOAuth && zendeskUserInfo && (
        <OAuthAuthorize
          isAdmin={zendeskUserInfo.isAdmin}
          subdomain={zendeskUserInfo.subdomain}
          token={authResponseData?.userToken}
          onAuthComplete={handleOAuthComplete}
        />
      )}
      {errorObj && <ErrorPage error={errorObj} />}
    </section>
  );
};

export default AuthForm;
